import { Box, BoxProps } from "@mui/material";
import { FeatureFlags } from "../../util/featureFlags";
import type {
  ExplorerSource,
  QueryResolution,
} from "../../models/Explorer";

/** "ss 2021–2022 · ctg 2023–2024", or the bare sources when no seasons are known. */
export function formatResolution(
  resolution?: Pick<QueryResolution, "sources" | "seasonsBySource"> | null,
): string {
  if (!resolution?.sources?.length) return "";
  return resolution.sources
    .map((source) => {
      const seasons = [...(resolution.seasonsBySource?.[source] ?? [])].sort(
        (a, b) => a - b,
      );
      if (seasons.length === 0) return source;
      const first = seasons[0];
      const last = seasons[seasons.length - 1];
      return first === last ? `${source} ${first}` : `${source} ${first}–${last}`;
    })
    .join(" · ");
}

/**
 * Provider label for dev comparisons. Renders nothing unless the build has the
 * `sourceLabel` flag: the product never names a provider (DESIGN §11 q3).
 */
export default function DevSourceLabel({
  resolution,
  source,
  explicit,
  sx,
}: {
  resolution?: QueryResolution | null;
  /** A single known source (a report blob, a roll-up scope) */
  source?: ExplorerSource | string | null;
  explicit?: boolean;
  sx?: BoxProps["sx"];
}) {
  if (!FeatureFlags.sourceLabel) return null;
  const text = resolution ? formatResolution(resolution) : source ?? "";
  if (!text) return null;
  const isExplicit = explicit ?? resolution?.explicit;
  return (
    <Box
      component="span"
      data-testid="dev-source-label"
      title="Dev only: the provider this data was read from"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 0.75,
        py: 0.125,
        border: "1px dashed #9e9e9e",
        borderRadius: "3px",
        fontSize: "0.7rem",
        fontFamily: "monospace",
        color: "#616161",
        whiteSpace: "nowrap",
        "@media print": { display: "none" },
        ...sx,
      }}
    >
      dev · {text}
      {isExplicit ? " (explicit)" : ""}
    </Box>
  );
}
